import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { cadsSemanticFamilyStem } from '../../utils/cads-semantic-color.util';
import {
  CADS_ICON_SPRITE_DEFAULT_URL,
  CADS_ICON_VIEW_BIT_SVG_PX,
  CadsIconViewBitConfig,
} from './cads-icon-view-bit.config';

/**
 * Icon bit: sprite glyph and/or centered **label** (step numbers) inside a sized frame. **rounded** / **circle** use
 * `--cads-color-{key}-pastel` fill and **base** 1px border when `color` is a `var(--cads-color-…)` token (same ramp as
 * info/badge bits); otherwise `color-mix` with **base** and surface. Optional **iconColor** overrides only the glyph fill. **icon**
 * **dot** shape renders only a small filled circle in the semantic **base** color (no pastel frame). **icon**
 * shape is transparent for composition (e.g. rows, container chevrons). Use **collapsed** with **icon** so the frame
 * matches the glyph (no fixed 32/40/48px box); optional **iconSizePx** overrides the sprite pixel size from the
 * small/medium/large/**xlarge** preset ({@link CADS_ICON_VIEW_BIT_SVG_PX}). Optional {@link CadsIconViewBitConfig.iconWidthPx} and
 * {@link CadsIconViewBitConfig.iconHeightPx} set a non-square sprite box (both required).
 * Sprite: {@link CADS_ICON_SPRITE_DEFAULT_URL} → `symbol-defs.svg` in this package’s `src/assets`.
 */
@Component({
    selector: 'cads-icon-view-bit',
    imports: [NgStyle],
    templateUrl: './cads-icon-view-bit.component.html',
    styleUrl: './cads-icon-view-bit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-icon-view-bit-host',
    }
})
export class CadsIconViewBitComponent {
  readonly config = input.required<CadsIconViewBitConfig>();

  readonly effectiveConfig = computed(() => {
    const c = this.config();
    return {
      icon: (c.icon ?? '').trim(),
      label: (c.label ?? '').trim(),
      color: c.color,
      iconColor: c.iconColor,
      iconSizePx: c.iconSizePx,
      iconWidthPx: c.iconWidthPx,
      iconHeightPx: c.iconHeightPx,
      collapsed: c.collapsed ?? false,
      size: c.size ?? 'medium',
      shape: c.shape ?? 'rounded',
      spriteUrl: c.spriteUrl ?? CADS_ICON_SPRITE_DEFAULT_URL,
      accessibleLabel: c.accessibleLabel,
    };
  });

  /** Resolved sprite width/height in px ({@link CadsIconViewBitConfig.iconSizePx} or {@link CADS_ICON_VIEW_BIT_SVG_PX}). */
  readonly svgSizePx = computed(() => {
    const e = this.effectiveConfig();
    const raw = e.iconSizePx;
    if (raw != null && Number.isFinite(raw) && raw > 0) {
      return Math.round(raw);
    }
    return CADS_ICON_VIEW_BIT_SVG_PX[e.size];
  });

  /**
   * Collapsed frame: only when {@link CadsIconViewBitConfig.collapsed} and **icon** shape (ignored for rounded/circle).
   */
  readonly isCollapsed = computed(() => {
    const e = this.effectiveConfig();
    return e.collapsed && e.shape === 'icon';
  });

  /** Compact status dot: {@link CadsIconViewBitConfig.shape} **dot** (ignores `icon` / `label` for content). */
  readonly isDot = computed(() => this.effectiveConfig().shape === 'dot');

  /**
   * When the resolved tint is a CADS `var(--cads-color-…)` token (including default mid-gray), rounded/circle use **pastel** fill.
   */
  readonly hasSemanticPaletteRamp = computed(() => {
    const e = this.effectiveConfig();
    const displayColor = e.color ?? 'var(--cads-color-mid-gray)';
    return cadsSemanticFamilyStem(displayColor) != null;
  });

  readonly cssVars = computed<Record<string, string>>(() => {
    const e = this.effectiveConfig();
    const displayColor = e.color ?? 'var(--cads-color-mid-gray)';
    const stem = cadsSemanticFamilyStem(displayColor);
    const baseToken = stem ? `var(--cads-color-${stem})` : displayColor;
    const glyph = e.iconColor ?? displayColor;

    const w = e.iconWidthPx;
    const h = e.iconHeightPx;
    const hasRect =
      w != null &&
      h != null &&
      Number.isFinite(w) &&
      Number.isFinite(h) &&
      w > 0 &&
      h > 0;

    const vars: Record<string, string> = {
      '--cads-icon-view-bit-base': baseToken,
      '--cads-icon-view-bit-glyph': glyph,
      '--cads-icon-view-bit-svg-size': `${this.svgSizePx()}px`,
    };
    if (hasRect && w != null && h != null) {
      vars['--cads-icon-view-bit-svg-width'] = `${Math.round(w)}px`;
      vars['--cads-icon-view-bit-svg-height'] = `${Math.round(h)}px`;
    }
    if (stem) {
      vars['--cads-icon-view-bit-pastel'] = `var(--cads-color-${stem}-pastel)`;
    }
    return vars;
  });

  readonly spriteHref = computed(() => {
    const e = this.effectiveConfig();
    if (e.label || !e.icon) {
      return '';
    }
    return `${e.spriteUrl}#${e.icon}`;
  });

  readonly showLabel = computed(() => !!this.effectiveConfig().label);

  readonly accessibleLabelText = computed(() => {
    const label = this.effectiveConfig().accessibleLabel;
    return label?.trim() ? label.trim() : null;
  });

  readonly isSmall = computed(() => this.effectiveConfig().size === 'small');
  readonly isMedium = computed(() => this.effectiveConfig().size === 'medium');
  readonly isLarge = computed(() => this.effectiveConfig().size === 'large');
  readonly isXlarge = computed(() => this.effectiveConfig().size === 'xlarge');
  readonly isRounded = computed(() => this.effectiveConfig().shape === 'rounded');
  readonly isCircle = computed(() => this.effectiveConfig().shape === 'circle');
  readonly isIconShape = computed(() => this.effectiveConfig().shape === 'icon');

  /** True when {@link CadsIconViewBitConfig.iconWidthPx} and {@link CadsIconViewBitConfig.iconHeightPx} supply a non-square sprite box. */
  readonly hasRectangularSprite = computed(() => {
    const e = this.effectiveConfig();
    const w = e.iconWidthPx;
    const h = e.iconHeightPx;
    return (
      w != null &&
      h != null &&
      Number.isFinite(w) &&
      Number.isFinite(h) &&
      w > 0 &&
      h > 0
    );
  });
}
